import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import PermissionGuard from "@/components/PermissionGuard";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { QueryTable } from "@/components/query-table/QueryTable";
import { EmailRecipientsAddUsersButton } from "@/modules/email-recipients/components/EmailRecipientsAddUsersButton";
import { EmailRecipientsRemoveUsersButton } from "@/modules/email-recipients/components/EmailRecipientsRemoveUsersButton";
import { EmailRecipientsToolbar } from "@/modules/email-recipients/components/EmailRecipientsToolbar";
import { EmailRecipientsToolbarActions } from "@/modules/email-recipients/components/EmailRecipientsToolbarActions";
import { useEmailRecipientsRemoveUsersMutation } from "@/modules/email-recipients/hooks/useEmailRecipientsRemoveUsersMutation";
import { SuccessFetchListResponse } from "@/services/api";
import { DisplayEmailRecipient } from "@/types/emailRecipient";
import { Operation } from "@/types/permissions";
import {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

interface EmailRecipientsListContentProps {
  emailTypeId: string;
  queryKey: QueryKey;
  onTypeChange: (nextTypeId: string | undefined) => void;
  emailRecipientsQuery: UseInfiniteQueryResult<
    InfiniteData<SuccessFetchListResponse<DisplayEmailRecipient>>
  >;
  onOpenAddUsers: () => void;
}

export const EmailRecipientsListContent = ({
  emailTypeId,
  queryKey,
  onTypeChange,
  emailRecipientsQuery,
  onOpenAddUsers,
}: EmailRecipientsListContentProps) => {
  const { store } = useRowSelectionContext();

  const { handleSubmit, isSubmitting } = useEmailRecipientsRemoveUsersMutation({
    store,
    emailTypeId,
    queryKey,
  });

  const queryValue = useMemo(
    () => ({ query: emailRecipientsQuery }),
    [emailRecipientsQuery],
  );

  useEffect(() => {
    store.clear();
  }, [emailTypeId, store]);

  return (
    <DataTableQueryContextProvider value={queryValue}>
      <EmailRecipientsToolbar
        emailTypeId={emailTypeId}
        onTypeChange={onTypeChange}
        actions={
          <EmailRecipientsToolbarActions
            emailTypeId={emailTypeId}
            onRemove={handleSubmit}
            isRemoveSubmitting={isSubmitting}
            onOpenAdd={onOpenAddUsers}
          />
        }
      />
      <QueryTable.Content>
        <QueryTable.Status>
          <PermissionGuard permission={Operation.Update}>
            <EmailRecipientsRemoveUsersButton
              onRemove={handleSubmit}
              isSubmitting={isSubmitting}
              variant="secondary"
            />
          </PermissionGuard>
          <PermissionGuard permission={Operation.Update}>
            <EmailRecipientsAddUsersButton
              onOpen={onOpenAddUsers}
              isDisabled={!emailTypeId}
              variant="secondary"
            />
          </PermissionGuard>
        </QueryTable.Status>
      </QueryTable.Content>
    </DataTableQueryContextProvider>
  );
};
